/**
 * Post-login bonus reminder — trigger side.
 *
 * Signal: GET /login-bonus/eligibility, the endpoint the "Claim Daily Bonus"
 * widget already lives on (elements/LoginBonus.js). It answers 200 with a
 * `campaign` object only while the player has a daily-login bonus that is still
 * claimable, and a non-200 / campaign-less body once there is nothing to claim.
 * That is a real "awarded and NOT yet used" signal about this player, and it
 * needs no new backend field.
 *
 * Why not the alternatives:
 *   GET  /bonus/active        -> describes a bonus the player has ALREADY taken
 *                                and is wagering off (has_active_bonus,
 *                                wagering_achieved). "Active" is the opposite of
 *                                "not used yet".
 *   POST /deposit/campaigns/1 -> campaigns the player is ELIGIBLE for. An offer
 *                                on the shelf, not something waiting in their
 *                                account; every eligible player would qualify.
 *   POST /list/free-spins     -> a paginated award history, free spins only.
 *   GET  /deposit/get-next-bonus -> not called anywhere in this frontend, so its
 *                                payload cannot be verified here.
 *
 * KNOWN SCOPE LIMIT: this reminds about an unclaimed DAILY-LOGIN bonus. A
 * pending deposit-match bonus or an unused free-spin batch does not surface
 * here — no existing endpoint reports those as "waiting and unused". Widening it
 * needs a backend field, not a different guess on the client.
 */

import api from "services/api";
import { POPUP_TYPES, canShowPopup, loadPopup, requestPopup } from "utils/popups";

/** True only while the player has a login bonus sitting there unclaimed. */
async function hasUnclaimedBonus() {
  try {
    // api.get never rejects; a 404/4xx comes back as { status, data }.
    const res = await api.get("/login-bonus/eligibility");
    return res?.status === 200 && Boolean(res.data?.campaign);
  } catch (e) {
    return false;
  }
}

/**
 * Call right after a successful login. Order matters: config first, then the
 * frequency cap, and only then the player probe — so a brand with the pop-up
 * disabled, or a player who already saw it, costs no extra request at all.
 */
export async function maybeShowPostLoginBonusReminder() {
  const popup = await loadPopup(POPUP_TYPES.POST_LOGIN_BONUS_REMINDER);
  if (!popup) return;
  if (!canShowPopup(popup)) return;
  if (!(await hasUnclaimedBonus())) return;

  // The cap was checked above only to skip the probe; PopupHost stays the one
  // place that decides to render and spends the cap.
  requestPopup(POPUP_TYPES.POST_LOGIN_BONUS_REMINDER);
}
